import { useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Copy, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function Vault() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", username: "", password: "", url_or_host: "", category: "Device" });

  const load = () => api.get("/vault").then(r => setItems(asArray(r.data)));
  useEffect(() => { load(); }, []);

  const reveal = async (id) => {
    const { data } = await api.get(`/vault/${id}/reveal`);
    await navigator.clipboard.writeText(data.password);
    toast.success("Password copied to clipboard");
  };

  const create = async () => {
    await api.post("/vault", form);
    setOpen(false);
    setForm({ label: "", username: "", password: "", url_or_host: "", category: "Device" });
    load();
    toast.success("Credential stored (encrypted)");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Credential Vault</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Secrets ({items.length})</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="vault-add-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2"><Plus size={14}/> Add credential</button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-900 text-zinc-100">
            <DialogHeader><DialogTitle>New Credential</DialogTitle></DialogHeader>
            <div className="space-y-2">
              {[["Label","label"],["Username","username"],["Password","password"],["URL / Host","url_or_host"]].map(([l,k]) => (
                <input key={k} placeholder={l} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" data-testid={`vault-input-${k}`} />
              ))}
              <select value={form.category} onChange={e=>setForm({...form, category: e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                {["Device","Service Account","API Key","Domain","VPN"].map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <DialogFooter><button onClick={create} data-testid="vault-save-btn" className="bg-blue-600 hover:bg-blue-700 text-white text-sm rounded px-3 py-2">Save</button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left px-4 py-3">Label</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Username</th><th className="text-left px-4 py-3">Password</th><th className="text-left px-4 py-3">URL / Host</th><th className="text-left px-4 py-3"></th></tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-4 py-2">{c.label}</td>
                <td className="px-4 py-2 text-zinc-400">{c.category}</td>
                <td className="px-4 py-2 font-mono text-xs">{c.username}</td>
                <td className="px-4 py-2 font-mono tracking-widest text-zinc-500">{c.password_masked}</td>
                <td className="px-4 py-2 font-mono text-xs text-blue-400">{c.url_or_host}</td>
                <td className="px-4 py-2 text-right"><button onClick={()=>reveal(c.id)} data-testid={`vault-reveal-${c.id}`} className="inline-flex items-center gap-1 text-xs bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-2 py-1"><Eye size={12}/> Reveal</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
